import * as amplitude from '@amplitude/analytics-browser';
import {
  ACTION_LOGIN,
  ACTION_SIGNUP,
  NumericUserProperties,
  UserProperty,
} from './eventTypesConstants';
import { AnalyticsSearchContext } from 'hooks/search/useSearchHandlers';
import { EventCustomAttributes } from './eventTypeConstantsAmplitude';
import { Network } from 'components/OAuth';
import { PaymentMethodDetails } from 'utils/Analytics';
import { SearchMode } from 'components/search/utils';
import { SearchRequestParams } from 'utils/search/buildSearchRequestParams';
import { consumer, core, safeToNumber } from '@tock/coretypes';
import { filterNilValues } from '@tock/shared/utils/typeUtils';
import { isIOSNative } from 'utils/deviceUtils';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { useAmplitudeSendEvent } from './AnalyticsContext';
import { useMountEffect } from '@tock/hooks';

let initialized = false;

export const getAppType = () => {
  if (isIOSNative()) {
    return 'Tock App (iOS)';
  }
  if (window.ReactNative) {
    return 'Tock App (React Native)';
  }
  return 'Web';
};

export const setupAmplitudeTracking = (key?: string, enableSessionReplay?: boolean) => {
  window.addEventListener('message', handleNativeEvent);

  if (!key) {
    return;
  }
  if (!initialized) {
    amplitude.init(key, {
      trackingOptions: {
        ipAddress: false,
      },
      autocapture: {
        attribution: true,
        pageViews: false,
        sessions: true,
        fileDownloads: false,
        formInteractions: false,
      },
    });

    if (enableSessionReplay) {
      const sessionReplayTracking = sessionReplayPlugin({ sampleRate: 0.02 });
      amplitude.add(sessionReplayTracking);
    }

    initialized = true;
  } else {
    amplitude.setOptOut(false);
  }

  amplitudeSetUserProperty({ property: 'Device app type', value: getAppType() });
};

export const revokeAmplitudeTracking = () => {
  amplitude.setOptOut(true);
};

export const amplitudeSendEvent = (event: string, data?: EventCustomAttributes) => {
  if (!initialized) {
    return;
  }

  amplitude.track(event, wrapData(data));
};

/**
 * Don't use this often. It usually makes more sense to call
 * `amplitudeSendEvent` directly.
 *
 * This hack is useful when we want to send an Amplitude event when a certain
 * part of the component tree gets rendered for the first time. The primary
 * motivation for writing this was to capture an event on pageload where the
 * relevant data was initially fetched in a server-side render.
 */
export const AmplitudeSendOnce: React.FC<{
  event: string;
  data?: EventCustomAttributes;
}> = ({ event, data }) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  useMountEffect(() => {
    amplitudeSendEvent(event, data);
  });
  return null;
};

const wrapData = (data?: EventCustomAttributes) => {
  if (!window || !window.location) {
    return data;
  }

  return { ...data, pathname: window.location.pathname, pageTitle: window.document.title };
};

export const amplitudeSetUserId = (uuid: string) => {
  amplitude.setUserId(uuid);
};

export const amplitudeSetUserProperty = (property: UserProperty) => {
  if (!initialized) {
    return;
  }
  const identify = new amplitude.Identify();
  identify.set(property.property, property.value);
  amplitude.identify(identify);
};

export const amplitudeSetUserPropertyOnce = (property: UserProperty) => {
  if (!initialized) {
    return;
  }
  const identify = new amplitude.Identify();
  identify.setOnce(property.property, property.value);
  amplitude.identify(identify);
};

export const amplitudeIncrementUserProperty = (
  property: NumericUserProperties,
  increment: number
) => {
  if (!initialized) {
    return;
  }
  const identify = new amplitude.Identify();
  identify.add(property, increment);
  amplitude.identify(identify);
};

export const amplitudeAuthEvent = (
  authEventType: typeof ACTION_LOGIN | typeof ACTION_SIGNUP,
  patron: core.IPatron | undefined,
  options?: { network?: Network; checkoutProperties?: EventCustomAttributes }
) => {
  const network = options?.network;
  // todo auth context (checkout, shop browsing, reservations browsing, modal vs page)
  const data = {
    auth: {
      network,
    },
    ...options?.checkoutProperties,
  };
  amplitudeSendEvent(authEventType, data);
  network && amplitudeSetUserProperty({ property: 'Auth network', value: network });
  amplitudeSetUserProperty({
    property: 'Has giftcard balance',
    value: patron?.hasTockGiftCardBalance || false,
  });
  patron?.id && amplitudeSetUserProperty({ property: 'PatronId', value: safeToNumber(patron.id) });
};

export const businessToData = (
  business?: consumer.IBusinessConfig | null
): EventCustomAttributes => ({
  businessId: business?.id || undefined,
  businessName: business?.name || undefined,
  businessGroupId: business?.businessGroup?.id || undefined,
  businessGroupName: business?.businessGroup?.name || undefined,
  businessType: business?.businessType || undefined,
  businessPriceRange: business?.priceRange || undefined,
  currencyCode: business?.currencyCode || undefined,
  metroArea: business?.nearestMetro || undefined,
  businessState: business?.state || undefined,
});

export const experienceToData = (experience?: core.ITicketType): EventCustomAttributes => {
  const holdIndex =
    experience?.ticketTypePriceConfig?.findIndex(
      (config) => config.priceType === 'PAYMENT_CARD_HOLD'
    ) ?? -1;
  const hasHold = holdIndex !== -1;

  // we should rethink how we record variety in a more variable world
  const variety = hasHold ? 'PAYMENT_CARD_HOLD' : experience?.variety;

  return {
    experienceId: safeToNumber(experience?.id) || undefined,
    experienceName: experience?.name || undefined,
    experienceVariety: variety || undefined,
    hasMobileTicket: experience?.isUsingMobileTicket || false,
  };
};

export const pastPurchaseToData = (purchase?: core.IPastPurchase): EventCustomAttributes => {
  return {
    ...experienceToData(purchase?.ticketType || undefined),
    ...businessToData(purchase?.business),
    purchaseId: safeToNumber(purchase?.id) || undefined,
    // parsing into amplitude format according to https://amplitude.com/blog/data-types
    reservationDateTime: purchase?.ticketDateTime?.replace('T', ' '),
    // todo party size
    seatingArea: purchase?.seatingAreaName || undefined,
    seatingAreaId: purchase?.seatingAreaId || undefined,
    purchaseKeyValue: purchaseKeyValueToMap(purchase?.keyValue || undefined),
  };
};

export const purchaseOrderToData = (purchaseOrder: core.PurchaseOrder): EventCustomAttributes => {
  return {
    purchaseId: safeToNumber(purchaseOrder.id),
    // parsing into amplitude format according to https://amplitude.com/blog/data-types
    reservationDateTime: purchaseOrder.dateTime.replace('T', ' '),
    // todo party size
    seatingArea: purchaseOrder.seatingAreaName || undefined,
    seatingAreaId: purchaseOrder.seatingAreaId
      ? safeToNumber(purchaseOrder.seatingAreaId)
      : undefined,
    purchaseKeyValue: purchaseKeyValueToMap(purchaseOrder.keyValue),
  };
};

/**
 * Transform purchase attribution data to an object of {attributes:values}
 * for filtering and grouping in amplitude.
 *
 * ie: [{tock_source: "tock"}, {tock_content: "search"}]
 */
export const purchaseKeyValueToMap = (keyValues: core.IPurchaseKeyValue[] | undefined) => {
  if (keyValues === undefined) {
    return undefined;
  }
  const result: EventCustomAttributes['purchaseKeyValue'] = [];

  keyValues.forEach(({ attribute, attributeValue }) => {
    if (attribute && attributeValue) {
      result.push({ [attribute]: attributeValue });
    }
  });

  return result;
};

export const paymentMethodToData = (details?: PaymentMethodDetails): EventCustomAttributes => {
  return {
    paymentMethod: details?.paymentMethod,
    availablePaymentMethods: details?.availablePaymentMethods,
    isSupportedApplePayEnvironment: details?.isSupportedApplePayEnvironment,
    isSavedCreditCard: details?.isSavedCreditCard,
    paymentProcessorType: details?.paymentProcessorType,
  };
};

const offeringVarietyToExperienceVariety = (
  offeringVariety: consumer.Offering.Type | undefined
): core.TicketType.Variety | undefined => {
  if (!offeringVariety) {
    return undefined;
  }
  switch (offeringVariety) {
    case 'PRIX_FIXE':
      return 'FULL';

    default:
      return offeringVariety;
  }
};

export const experienceOfferingToData = (
  experience?: consumer.Offering | consumer.IOffering
): EventCustomAttributes => {
  // todo get hold information on Offering
  return {
    experienceId: experience?.id ?? undefined,
    experienceName: experience?.name ?? undefined,
    experienceVariety: experience?.type
      ? offeringVarietyToExperienceVariety(experience?.type)
      : undefined,
    seatingAreaOptions: experience?.seatingArea
      ? experience?.seatingArea.map((seatingArea) => seatingArea.name).filter(filterNilValues)
      : [],
    // hasMobileTicket not available on Offering currently
  };
};

export const amplitudeSearchResultsEvent = (
  searchQuery: SearchRequestParams,
  searchContext: AnalyticsSearchContext,
  resultCount: number
): EventCustomAttributes => {
  return {
    searchContext,
    offeringSearchType: searchQuery.query.type,
    minDateTime: searchQuery.query.dateTime?.min.toString(),
    maxDateTime: searchQuery.query.dateTime?.max.toString(),
    minPartySize: searchQuery.query.size?.min,
    maxPartySize: searchQuery.query.size?.max,
    city: searchQuery.additionalQuery.city?.[0],
    resultCount,
  };
};

export const getAnalyticsEventType = (mode?: SearchMode): AnalyticsSearchContext => {
  switch (mode?.stateType) {
    case 'group':
      return 'Business group search';
    case 'togo':
      return 'To Go search';
    case 'collectionPage':
      return 'Collection page search';
    case 'city':
    default:
      return 'Citywide search';
  }
};

const handleNativeEvent = (event: MessageEvent) => {
  if (!event) {
    return;
  }

  if (event?.data?.nativeEvent) {
    const nativeEvent = event.data.nativeEvent as NativeEvent;
    amplitudeSendEvent(nativeEvent.name, nativeEvent.data);
  }
};

interface NativeEvent {
  name: string;
  data: EventCustomAttributes;
}
